<template>
  <div class="eolienne-list">
    <card class="card" title="Eolienne(s)">
      <!-- TABLEAU AFFICHAGE -->
      <b-table-simple hover small caption-top responsive v-if="isEoliennes">
        <b-thead variant="dark">
          <b-tr>
            <b-th>Nom/Identifiant</b-th>
            <b-th class="table-action-column">Action(s)</b-th>
          </b-tr>
        </b-thead>
        <b-tbody>
          <b-tr
            v-for="eolienne in eoliennes"
            :key="eolienne.id"
            @mouseover="onLineHover(eolienne.id)"
            @mouseout="onLineOut()"
            :class="{
              'selected-line': isSelectedLine(eolienne.id)
            }"
          >
            <b-td v-if="!eolienne.nomEolienne">{{ eolienne.id }}</b-td>
            <b-td v-if="eolienne.nomEolienne">{{ eolienne.nomEolienne }}</b-td>
            <b-td class="table-action-column">
              <b-button
                class="me-2 "
                :class="{
                  'info-btn--saisie': setOnlyReadStyle
                }"
                variant="success"
                size="sm"
                outline
                icon
                :id="`eolienne-show-${eolienne.id}`"
                v-on:click="prepareShow(eolienne, 'modal-eolienne-show')"
              >
                <i class="fa fa-info"></i>
                <b-tooltip :target="`#eolienne-show-${eolienne.id}`">
                  Afficher la fiche de l'éolienne {{ getNomEolienneElseId(eolienne) }}
                </b-tooltip>
              </b-button>

              <b-button
                class="me-2"
                v-if="showEditBtn"
                variant="success"
                size="sm"
                outline
                icon
                :id="`eolienne-edit-${eolienne.id}`"
                v-on:click="prepareEdit(eolienne)"
                :disabled="isLock"
              >
                <i class="fa fa-edit"></i>
                <b-tooltip :target="`#eolienne-edit-${eolienne.id}`">
                  Éditer les informations de l'éolienne {{ getNomEolienneElseId(eolienne) }}
                </b-tooltip>
              </b-button>

              <b-button
                v-if="showDeleteBtn"
                variant="danger"
                size="sm"
                outline
                icon
                :id="`eolienne-delete-${eolienne.id}`"
                @click="prepareShow(eolienne, 'modal-eolienne-delete')"
                :disabled="isLock"
              >
                <i class="fa fa-trash"></i>
                <b-tooltip :target="`#eolienne-delete-${eolienne.id}`">
                  Supprimer l'éolienne {{ getNomEolienneElseId(eolienne) }}
                </b-tooltip>
              </b-button>
            </b-td>
          </b-tr>
        </b-tbody>
        <b-tfoot v-if="showTableAddBtn">
          <b-tr>
            <b-td></b-td>
            <b-td>
              <b-button-group>
                <span id="eolienne-add-helper">
                  <b-tooltip target="#eolienne-add-helper">
                    {{ disableAddAerogenerateurBtn
                        ? 'Pour ajouter une nouvelle éolienne, veuillez déclarer un nombre supérieur dans la fiche du parc' :
                    '' }}
                  </b-tooltip>
                  <b-button
                    class="me-2"
                    variant="success"
                    outline
                    icon
                    id="eolienne-add-new"
                    @click="prepareAdd()"
                    :disabled="disableAddAerogenerateurBtn || isLock"
                  >
                    Ajouter
                    <b-tooltip target="#eolienne-add-new">
                      {{disableAddAerogenerateurBtn ? '' : 'Ajouter une nouvelle éolienne'}}
                    </b-tooltip>
                  </b-button>
                </span>
                <b-button
                  class="rounded duplication-btn"
                  variant="success"
                  icon
                  id="eolienne-duplicate"
                  @click="prepareDuplicate()"
                  v-if="!disableAddAerogenerateurBtn"
                  :disabled="isLock"
                >
                  <i class="ti-loop"></i>
                  <b-tooltip target="#eolienne-duplicate">
                    Dupliquer la dernière éolienne saisie
                  </b-tooltip>
                </b-button>
              </b-button-group>
            </b-td>
          </b-tr>
        </b-tfoot>
      </b-table-simple>

      <!-- Alertes-->
      <div
        class="alert alert-warning"
        role="alert"
        v-if="!validNumberAerogenerateur || !isEoliennesDeclared"
      >
        <span v-if="isEoliennesDeclared">
          <span v-if="isExploitant">Vous avez</span>
          <span v-else>L'exploitant a</span>

          <span> déclaré que le parc était composé de </span>
          <span class="fw-bold">{{ parc.nombreAerogenerateur }} </span>
          <span> éolienne(s). A ce stade, </span>

          <span v-if="isExploitant">vous avez</span>
          <span v-else>il a déjà </span>

          <span> saisi </span>
          <span class="fw-bold">{{ eoliennes.length }} </span>
          <span> éoliennes.</span>
        </span>
        <span v-else>
          Aucune éolienne n'est déclarée dans la fiche du parc.
        </span>
      </div>

      <div
        class="alert alert-success"
        role="alert"
        v-if="validNumberAerogenerateur && isEoliennes"
      >
        Le nombre d'éoliennes déclaré dans la fiche du parc correspond au nombre
        saisi.
      </div>

      <!-- Bouton ajouter si tableau vide -->
      <div v-if="showFirstAddBtn" class="d-flex justify-content-start mb-2">
        <b-button-group>
          <span id="eolienne-add-help">
            <b-tooltip target="#eolienne-add-help">
              {{!isInventaireCommunes ? 'Veuillez spécifier au moins une commune d\'implantation dans la fiche du parc' : ''}}
            </b-tooltip>
            <b-button
              variant="success"
              outline
              icon
              :disabled="!isInventaireCommunes || isLock"
              id="eolienne-add"
              v-on:click="prepareAdd()"
            >
              Ajouter
              <b-tooltip target="#eolienne-add">
                Ajouter une nouvelle éolienne
              </b-tooltip>
            </b-button>
          </span>
        </b-button-group>
      </div>
      <b-row v-if="showHelpField">
        <b-col class="mx-2 mt-1 mb-3 help-field">
          * L'ordre de saisie des caractérisques des éoliennes doit correspondre
          au numéro des éoliennes (E1, E2, etc.) qui figurent dans les dossiers
          administratifs.
        </b-col>
      </b-row>
    </card>

    <eolienne-list-modal
      :eolienne="eolienne"
      :getNomEolienneElseId="getNomEolienneElseId"
      :mapZoom="mapZoom"
      :mapCenter="mapCenter"
      :markers="markers"
    />

    <eolienne-delete-modal
      :addNotification="addNotification"
      :eolienne="eolienne"
    />

    <eolienne-edit-modal
      @remove-marker="removeMarker"
      @on-input-coordinates="onInputCoordinates"
      @set-loading-validation="setLoadingValidation"
      @update-coordinates="updateCoordinates"
      @check-coordinates="checkCoordinates"
      @on-click-map="onClickMap"
      @reload-parc="$emit('reload-parc')"
      :addNotification="addNotification"
      :types-feux="typesFeux"
      :is-loading-coordinates-validation="isLoadingCoordinatesValidation"
      :eolienne="eolienne"
      :getNomEolienneElseId="getNomEolienneElseId"
      :map-center="mapCenter"
      :map-zoom="mapZoom"
      :markers="markers"
      :coordinatesAlert="coordinatesAlert"
      :is-update-from="isUpdateFrom"
    />

  </div>
</template>
<script setup>
import { Card } from "@/components/index";
import { aerogenerateurService } from "@/service/aerogenerateur.service.js";
import { mapService } from "@/service/map.service.js";
import { commonService } from "@/service/common.service.js";
import proj4 from 'proj4';

import {
  BButton, BButtonGroup, BCol,
  BRow,
  BTableSimple, BTbody,
  BTd, BTfoot,
  BTh,
  BThead, BTooltip,
  BTr, useModal
} from "bootstrap-vue-next";
import EolienneListModal from "@/components/Modals/EolienneListModal.vue";
import EolienneDeleteModal from "@/components/Modals/EolienneDeleteModal.vue";
import {computed, onMounted, ref} from "vue";
import {useReactiveStoreGetters} from "@/composables/useStore";
import store from "@/store";
import EolienneEditModal from "@/components/Modals/EolienneEditModal.vue";
import {lexiqueService} from "@/service/lexique.service";

const {show: showEditModal} = useModal("modal-eolienne-edit");
const {show: showListModal} = useModal("modal-eolienne-show");
const {show: showDeleteModal} = useModal("modal-eolienne-delete");

const emit = defineEmits(['reload-parc'])


// State variables
const eolienne = ref(null)
const referencesModeles = ref([])
const markers = ref([])
const isLoadingCoordinatesValidation = ref(null)
const isUpdateFrom = ref(true)
const communeGeoms = ref([])
const coordinatesAlert = ref({})
const typesFeux = ref([])

// Lifecycle hook
onMounted(() => {
  init()
})

// Map Getters
const {
  parc,
  eoliennes,
  epsg,
  isExploitant,
  isGestionnaire,
  isAdministrateur,
  isAdministrateurPortail,
  isAdministrateurRegionParc,
  isBrouillon,
  isEoliennes,
  isEoliennesDeclared,
  isInventaireCommunes,
  isLock,
  projections,
  defaultMapZoom,
  selectedMapElement,
  eolienneNextId,
} = useReactiveStoreGetters([
  'parc',
  'eoliennes',
  'epsg',
  'isExploitant',
  'isDreal',
  'isGestionnaire',
  'isAdministrateur',
  'isAdministrateurPortail',
  'isAdministrateurRegionParc',
  'isBrouillon',
  'isEoliennes',
  'isEoliennesDeclared',
  'isInventaireCommunes',
  'isLock',
  'projections',
  'baseLayer',
  'defaultMapZoom',
  'selectedMapElement',
  'eolienneNextId'
]);

// Computed properties
const isEnabledDrealElement = computed(() =>
  (isAdministrateur.value && isAdministrateurRegionParc.value) ||
  isAdministrateurPortail.value ||
  isGestionnaire.value
)

const setOnlyReadStyle = computed(() =>
  !isBrouillon.value && !isEnabledDrealElement.value
)

const showEditBtn = computed(() =>
  isBrouillon.value || isEnabledDrealElement.value
)

const showDeleteBtn = computed(() =>
  isBrouillon.value || isEnabledDrealElement.value
)

const showTableAddBtn = computed(() =>
  isBrouillon.value || isEnabledDrealElement.value
)

const showFirstAddBtn = computed(() =>
  (isBrouillon.value || isEnabledDrealElement.value) &&
  !isEoliennes.value &&
  isEoliennesDeclared.value
)

const showHelpField = computed(() =>
  (isBrouillon.value || isEnabledDrealElement.value) &&
  isEnoughAerogenerateurDeclared.value
)

const mapCenter = computed(() => {
  if (markers.value.length > 0) {
    return [markers.value[0][0], markers.value[0][1]]
  } else if (communeGeoms.value[0]) {
    return mapService.getMultipolygonCenter(communeGeoms.value[0])
  }
  return projections.value[epsg.value].wgs84Center
})

const isCoordinates = computed(() => {
  if (!eolienne.value) return false
  return eolienne.value.hasOwnProperty("yaerogenerateur") &&
    eolienne.value.yaerogenerateur != null &&
    eolienne.value.yaerogenerateur != "" &&
    eolienne.value.hasOwnProperty("xaerogenerateur") &&
    eolienne.value.xaerogenerateur != null &&
    eolienne.value.xaerogenerateur != ""
})


const mapZoom = computed(() => {
  if (isCoordinates.value || communeGeoms.value[0]) {
    return 13
  }
  return defaultMapZoom.value
})

const validNumberAerogenerateur = computed(() =>
  eoliennes.value.length === parc.value.nombreAerogenerateur
)

const isEnoughAerogenerateurDeclared = computed(() =>
  eoliennes.value.length < parc.value.nombreAerogenerateur
)

const disableAddAerogenerateurBtn = computed(() =>
  validNumberAerogenerateur.value || !isEnoughAerogenerateurDeclared.value
)

const parcCommuneGeom = computed(() => {
  if (communeGeoms.value) {
      return communeGeom.value.coordinates[0][0].map(point => {
        return [point[1], point[0]];
      });
  }
});

// Methods
const init = async () => {
  mapService.setDefaultIcons();
  const response = await lexiqueService.getLexTypeFeu();
  typesFeux.value = response.map(type => {
    return { value: type, text: type.libelle };
  });
};

const onLineHover = (elementId) => {
  store.dispatch("saveSelectedMapElement", elementId);
};
const onLineOut = () => {
  store.dispatch("clearSelectedMapElement");
};

const isSelectedLine = (elementId) => {
  return selectedMapElement.value === elementId;
};

const getNomEolienneElseId = (eolienne) => {
  if (eolienne.nomEolienne) {
    return eolienne.nomEolienne;
  } else {
    return eolienne.id;
  }
};

const wgs64FromXY = computed(() => {
  if (isCoordinates.value) {
    return proj4(`EPSG:${epsg.value}`).inverse([
      parseFloat(eolienne.value.xaerogenerateur),
      parseFloat(eolienne.value.yaerogenerateur)
    ])
  }
  return null
});

// Ajout d'un marker sur la carte
const addMarker = () => {
  if (isCoordinates.value) {
    markers.value = [];
    markers.value.push([
      wgs64FromXY.value[1],
      wgs64FromXY.value[0]
    ]);
  }
};

// Préparation de la modale d'informations
const prepareShow = async (eolienneValue, id) => {
  eolienne.value = eolienneValue;
  addMarker();
  await mapService.setMapLayout();
  switch (id) {
    case "modal-eolienne-show":
      showListModal();
      break;
    case "modal-eolienne-delete":
      showDeleteModal();
      break;
    default:
      break;
  }
};

// Préparation du formulaire de modification
const prepareEdit = async (eolienneValue) => {
  isUpdateFrom.value = true;
  eolienne.value = eolienneValue;

  // Mettre à jour les markers si les coordonnées existent
  if (eolienneValue.xaerogenerateur && eolienneValue.yaerogenerateur) {
    const localWgs84 = proj4(`EPSG:${epsg.value}`).inverse([
      parseFloat(eolienneValue.xaerogenerateur),
      parseFloat(eolienneValue.yaerogenerateur)
    ]);

    if (localWgs84) {
      markers.value.push([
        localWgs84[1],
        localWgs84[0]
      ]);
    }
  }

  checkCoordinates(eolienneValue);
  await mapService.setMapLayout();
  showEditModal();
};

// Préparation du formulaire de duplication
const prepareDuplicate = async () => {
  isUpdateFrom.value = false;
  // Création d'une nouvelle éolienne à partir de l'antécédente
  const eolienneId = parc.value.id + "_E" + eolienneNextId.value;
  eolienne.value = eoliennes.value[eoliennes.value.length - 1];
  eolienne.value.id = eolienneId;

  addMarker();
  checkCoordinates(eolienne.value);
  await mapService.setMapLayout();
  showEditModal();
};

// Préparation du formulaire d'ajout
const prepareAdd = async () => {
  isUpdateFrom.value = false;
  const eolienneId = parc.value.id + "_E" + eolienneNextId.value;

  eolienne.value = {
    id: eolienneId,
    parc: { id: parc.value.id },
    // Par défaut, on n'affiche pas la période d'allumage
    isAllumage: false,
    periodeAllumage: null,
    typeFeu: typesFeux.value[0].value,
    referenceModele: null,
    constructeur: null,
    constructeurAutre: null,
    referenceAutre: null,
    xaerogenerateur : null,
    yaerogenerateur : null,
  };

  await mapService.setMapLayout();
  showEditModal();
};

const getReferencesModeles = (constructeurId) => {
  referencesModeles.value = [];
  aerogenerateurService
    .getReferencesModeles(constructeurId)
    .then(response => {
      if (response) {
        referencesModeles.value = response.map(reference => {
          return { value: reference, text: reference.referenceCommerciale };
        });
        referencesModeles.value.push({
          value: "autre",
          text: "00_référence commerciale non référencée"
        });
      }
    })
    .catch(err => {
      console.log(err);
    });
};

const onClickMap = (event, eolienneParam) => {
  if (markers.value.length === 0) {
    const localXY = proj4(`EPSG:${epsg.value}`).forward([+event.latlng.lng, +event.latlng.lat]);

    const decimals = 2;
    const factor = (Math.pow(10, decimals));

    eolienneParam.xaerogenerateur = Math.round(localXY[0] * factor) / factor;
    eolienneParam.yaerogenerateur = Math.round(localXY[1] * factor) / factor;

    markers.value.push([
      +event.latlng.lat,
      +event.latlng.lng
    ]);
    checkCoordinates(eolienneParam);
  }
};

const removeMarker = (index, eolienneParam) => {
  if (!isUpdateFrom.value) {
    markers.value.splice(index, 1);
    coordinatesAlert.value = {};
    isLoadingCoordinatesValidation.value = null;
    eolienneParam.yaerogenerateur = null;
    eolienneParam.xaerogenerateur = null;
  }
};

const updateCoordinates = (location, eolienneParam) => {
  const localXY = proj4(`EPSG:${epsg.value}`).forward([+location.latlng.lng, +location.latlng.lat]);

  const decimals = 2;
  const factor = (Math.pow(10, decimals));

  eolienneParam.xaerogenerateur = Math.round(localXY[0] * factor) / factor;
  eolienneParam.yaerogenerateur = Math.round(localXY[1] * factor) / factor;
};

const setLoadingValidation = () => {
  isLoadingCoordinatesValidation.value = true;
};

const checkCoordinates = (eolienneParam) => {
  coordinatesAlert.value = {};
  isLoadingCoordinatesValidation.value =
    eolienneParam.xaerogenerateur !== "" ||
    eolienneParam.yaerogenerateur !== "";

  if (eolienneParam.xaerogenerateur && eolienneParam.yaerogenerateur) {
    const localWgs84 = proj4(`EPSG:${epsg.value}`).inverse([
      parseFloat(eolienneParam.xaerogenerateur),
      parseFloat(eolienneParam.yaerogenerateur)
    ]);

    mapService
      .checkCoordinates(
        parc.value.id,
        localWgs84[0],
        localWgs84[1]
      )
      .then(response => {
        if (response) {
          if (response.status === 200) {
            response.json().then(data => {
              coordinatesAlert.value = {
                state: "success",
                text: `L'éolienne se situe bien dans la liste des communes référencées dans le parc (${data.nomCommune}).`
              };
              eolienneParam.inventaireCommune = data;
            });
          } else if (response.status === 204) {
            response.text().then(message => {
              coordinatesAlert.value = {
                state: "danger",
                text: `L'éolienne ne se situe pas dans la liste des communes référencées dans le parc.`
              };
            });
          } else {
            commonService.handleUnsuccessfulResponse(response);
          }
        } else {
          commonService.handleNoResponse();
        }
        isLoadingCoordinatesValidation.value = false;
      })
      .catch(err => {
        console.log(err);
      });
  } else {
    isLoadingCoordinatesValidation.value = false;
  }
};

const onInputCoordinates = (eolienneParam) => {
  if (eolienne.value && eolienne.value.id === eolienneParam.id) {
    eolienne.value = { ...eolienneParam };
  }

  // Recalculer les markers en fonction de l'éolienne passée
  if (eolienneParam.xaerogenerateur && eolienneParam.yaerogenerateur) {
    const localWgs84 = proj4(`EPSG:${epsg.value}`).inverse([
      parseFloat(eolienneParam.xaerogenerateur),
      parseFloat(eolienneParam.yaerogenerateur)
    ]);

    markers.value = [];
    markers.value.push([
      localWgs84[1],
      localWgs84[0]
    ]);
  }

  checkCoordinates(eolienneParam);
};

const addNotification = (response, httpMethod) => {
  if (response) {
    // OK
    if (response.status == "200") {
      response.text().then(message => {
        const notificationTextMessage = message;
        const notificationIcon = "ti-check";
        const notificationType = "success";

        store.dispatch("saveTextNotification", {
          notificationIcon,
          notificationType,
          notificationTextMessage
        });

        emit('reload-parc');
      });
      // BAD REQUEST
    } else if (response.status == "400") {
      // POST/PUT
      if (httpMethod === "POST" || httpMethod === "PUT") {
        response.json().then(message => {
          const notificationJsonMessage = message;
          const notificationTitle =
            "Les modifications n'ont pas été prises en compte";
          const notificationIcon = "ti-alert";
          const notificationType = "danger";

          store.dispatch("saveJsonNotification", {
            notificationTitle,
            notificationIcon,
            notificationType,
            notificationJsonMessage
          });
        });
        // DELETE
      } else {
        const notificationTextMessage = "La suppression a échouée";
        const notificationIcon = "ti-alert";
        const notificationType = "danger";

        store.dispatch("saveTextNotification", {
          notificationIcon,
          notificationType,
          notificationTextMessage
        });

        console.log(response);
      }
      // Other HTTP response
    } else {
      let notificationIcon = "ti-alert pt-1";
      let notificationType = "danger";
      let notificationTextMessage = "Une erreur est survenue";

      // FORBIDDEN
      if (response.status == "403") {
        notificationIcon = "ti-lock pt-1";
        notificationTextMessage =
          "Vous avez tenté d'accéder à une ressource non autorisée";

        // INTERNAL ERROR
      } else if (response.status == "500") {
        notificationTextMessage = "Le serveur a rencontré une erreur";
      }

      store.dispatch("saveTextNotification", {
        notificationIcon,
        notificationType,
        notificationTextMessage
      });

      throw Error(`Request rejected with status ${response.status}`);
    }
  } else {
    const notificationIcon = "ti-alert pt-1";
    const notificationType = "danger";
    const notificationTitle = "La connexion a été interrompue";
    const notificationJsonMessage = ["Veuillez réactualiser la page"];

    store.dispatch("saveJsonNotification", {
      notificationTitle,
      notificationIcon,
      notificationType,
      notificationJsonMessage
    });
  }
};
</script>
<style lang="scss">
.eolienne-list {
  .info-btn {
    &--saisie {
      margin-left: 10px;
    }
  }
  .help-field {
    color: #343a40;
    font-style: italic;
    font-size: 0.8em;
  }
  .duplication-btn {
    $padding-x: 17px;
    padding: 0 $padding-x 0 $padding-x;
  }
  .selected-line {
    background: #ececec;
  }
}
</style>
